import { useState } from 'react'
import { type ReactNode } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import {
  LayoutDashboard,
  ToggleLeft,
  SlidersHorizontal,
  BrainCircuit,
  Building2,
  LogOut,
  Settings,
  Users,
  ShieldCheck,
  ScrollText,
  BarChart3,
  Smartphone,
  MessageSquare,
  Rocket,
} from 'lucide-react'
import { AppShell, Avatar, Sidebar, Topbar } from '@navegasistemas/dls-navega-rjs'
import { useAuthStore } from '@/store/auth-store'
import { useAuth } from '@/hooks/use-auth'

interface NavChildItem {
  key: string
  label: string
}

interface NavItem {
  key: string
  label: string
  icon: ReactNode
  children?: NavChildItem[]
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Geral',
    items: [
      {
        key: '/admin/dashboard',
        label: 'Dashboard',
        icon: <LayoutDashboard />,
      },
    ],
  },
  {
    label: 'Plataforma',
    items: [
      {
        key: '/admin/institutions',
        label: 'Instituições',
        icon: <Building2 />,
      },
      { key: '/admin/users', label: 'Usuários', icon: <Users /> },
      { key: '/admin/roles', label: 'Perfis de acesso', icon: <ShieldCheck /> },
      {
        key: '/admin/feature-flags',
        label: 'Feature Flags',
        icon: <ToggleLeft />,
      },
      {
        key: '/admin/parameters',
        label: 'Parâmetros',
        icon: <SlidersHorizontal />,
      },
      {
        key: 'group-member-service',
        label: 'Análise Atendimento',
        icon: <MessageSquare />,
        children: [
          { key: '/admin/member-service', label: 'Dashboard' },
          {
            key: '/admin/member-service/conversation-traceability',
            label: 'Rastreabilidade',
          },
        ],
      },
    ],
  },
  {
    label: 'Observabilidade',
    items: [
      { key: '/admin/logs', label: 'Logs', icon: <ScrollText /> },
      { key: '/admin/analytics', label: 'Analytics', icon: <BarChart3 /> },
      { key: '/admin/ai-costs', label: 'Custos de IA', icon: <BrainCircuit /> },
    ],
  },
  {
    label: '',
    items: [
      {
        key: 'group-equipe-navega',
        label: 'Equipe Navega',
        icon: <Users />,
        children: [
          { key: '/admin/navega-staff', label: 'Membros' },
          { key: '/admin/staff-roles', label: 'Perfis' },
          { key: '/admin/m2m-clients', label: 'Clientes M2M' },
        ],
      },
      {
        key: 'group-mobile',
        label: 'Mobile',
        icon: <Smartphone />,
        children: [
          { key: '/admin/notifications', label: 'Notificações' },
          { key: '/admin/device-tokens', label: 'Dispositivos' },
          { key: '/admin/app-versions', label: 'Versionamento' },
        ],
      },
      {
        key: 'group-deploy-hub',
        label: 'Deploy Hub',
        icon: <Rocket />,
        children: [
          { key: '/admin/deploy-hub', label: 'Dashboard' },
          { key: '/admin/deploy-hub/clients', label: 'Clientes' },
          { key: '/admin/deploy-hub/applications', label: 'Aplicações' },
          { key: '/admin/deploy-hub/deployments', label: 'Deployments' },
          { key: '/admin/deploy-hub/env-sync', label: 'Env Sync' },
          { key: '/admin/deploy-hub/logs', label: 'Logs de aplicações' },
        ],
      },
    ],
  },
]

const BREADCRUMB_LABELS: Record<string, string> = {
  '/admin/dashboard': 'Dashboard',
  '/admin/feature-flags': 'Feature Flags',
  '/admin/parameters': 'Parâmetros',
  '/admin/ai-costs': 'Custos de IA',
  '/admin/institutions': 'Instituições',
  '/admin/navega-staff': 'Equipe Navega',
  '/admin/staff-roles': 'Perfis Navega',
  '/admin/roles': 'Perfis de acesso',
  '/admin/users': 'Usuários',
  '/admin/logs': 'Logs',
  '/admin/analytics': 'Analytics',
  '/admin/notifications': 'Notificações',
  '/admin/device-tokens': 'Dispositivos',
  '/admin/app-versions': 'Versionamento APP',
  '/admin/member-service': 'Atendimento WhatsApp',
  '/admin/member-service/conversation-traceability': 'Rastreabilidade de Conversa',
  '/admin/m2m-clients': 'Clientes M2M',
  '/admin/preferences': 'Preferências',
  '/admin/deploy-hub': 'Deploy Hub',
  '/admin/deploy-hub/clients': 'Clientes',
  '/admin/deploy-hub/applications': 'Aplicações',
  '/admin/deploy-hub/deployments': 'Deployments',
  '/admin/deploy-hub/env-sync': 'Env Sync',
  '/admin/deploy-hub/logs': 'Logs de aplicações',
  '/admin/deploy-hub/applications/:appId': 'Detalhe da Aplicação',
}

export function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { clearAuth } = useAuthStore()
  const { user } = useAuth()

  function logout() {
    clearAuth()
    navigate('/admin')
  }

  const navGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      onClick: () => navigate(item.key),
      children: item.children?.map((child) => ({
        ...child,
        onClick: () => navigate(child.key),
      })),
    })),
  }))

  const allNavKeys = NAV_GROUPS.flatMap((g) =>
    g.items.flatMap((item) => (item.children ? item.children.map((c) => c.key) : [item.key])),
  )
  const activeKey =
    allNavKeys
      .filter((key) => pathname === key || pathname.startsWith(key + '/'))
      .sort((a, b) => b.length - a.length)[0] ?? pathname

  const breadcrumbLabel =
    BREADCRUMB_LABELS[pathname] ??
    Object.entries(BREADCRUMB_LABELS)
      .filter(([key]) => pathname.startsWith(key + '/'))
      .sort((a, b) => b[0].length - a[0].length)[0]?.[1] ??
    'Admin'

  const breadcrumb = [{ label: breadcrumbLabel }]

  const footerItems = [
    {
      key: '/admin/preferences',
      label: 'Preferências',
      icon: <Settings />,
      onClick: () => navigate('/admin/preferences'),
    },
    {
      key: 'logout',
      label: 'Sair',
      icon: <LogOut />,
      onClick: () => logout(),
    },
  ]

  return (
    <>
      <AppShell
        sidebarCollapsed={collapsed}
        sidebar={
          <Sidebar
            groups={navGroups}
            activeKey={activeKey}
            collapsed={collapsed}
            onCollapsedChange={setCollapsed}
            logo={
              <span className="text-sm font-semibold text-primary-700 tracking-tight">
                {collapsed ? 'N' : 'Navega Admin'}
              </span>
            }
            footerItems={footerItems}
          />
        }
        topbar={
          <Topbar
            breadcrumb={breadcrumb}
            onMenuToggle={() => setCollapsed((c) => !c)}
            searchSlot={null}
            rightSlot={<Avatar name={user?.name ?? user?.email ?? 'Admin'} size="sm" />}
          />
        }
      >
        <Outlet />
      </AppShell>
    </>
  )
}
